'use client';

import React from 'react';
import { parseHtml, nodesToReactElements } from '@/utils';

/**
 * SafeHtmlRenderer 컴포넌트 Props 인터페이스
 * 
 * 안전한 HTML 렌더링에 필요한 데이터를 정의합니다.
 */
interface SafeHtmlRendererProps {
  html: string;
  className?: string;
}

/**
 * 안전한 HTML 렌더링 컴포넌트
 * 
 * HTML 문자열을 안전하게 파싱하여 React 요소로 변환하는 컴포넌트입니다.
 * XSS 공격을 방지하고, 파싱 오류 시 적절한 폴백을 제공합니다.
 * 
 * 주요 기능:
 * - HTML 문자열을 DOM 노드로 파싱
 * - DOM 노드를 React 요소로 변환
 * - 각 요소에 고유한 key 속성 할당
 * - 파싱 오류 시 에러 메시지 표시
 * - TipTap 콘텐츠 스타일링 지원
 * - 색상 정보 파싱 및 적용
 * 
 * @example
 * ```tsx
 * // 기본 사용
 * <SafeHtmlRenderer html="<p style='color: red'>빨간 텍스트</p>" />
 * ```
 */
export function SafeHtmlRenderer({ html, className = '' }: SafeHtmlRendererProps) {
  // HTML이 없는 경우 빈 div 반환
  if (!html) {
    return <div className={className} />;
  }

  try {
    // HTML을 DOM 노드로 파싱
    const nodes = parseHtml(html);

    // DOM 노드를 React 요소로 변환
    const elements = nodesToReactElements(nodes);

    // 각 요소에 고유한 key 속성 할당
    const elementsWithKeys = elements.map((element, index) => {
      if (React.isValidElement(element) && !element.key) {
        return React.cloneElement(element, { key: `safe-html-${index}` });
      }
      return element;
    });

    // 렌더링된 HTML 콘텐츠 반환
    return (
      <div className={`tiptap-content ${className}`}>
        {elementsWithKeys}
      </div>
    );
  } catch (error) {
    // 파싱 오류 시 에러 메시지 표시
    return (
      <div className={className}>
        <p className="text-red-500">내용을 표시할 수 없습니다.</p>
      </div>
    );
  }
} 